import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Botón oficial de vinculación conforme a las Directrices de Marca de Strava API (Section 1).
 * Utiliza los colores, proporciones, isotipo vectorial y texto reglamentario ("Connect with Strava").
 */
export const StravaConnectButton = ({ 
  onClick, 
  disabled = false, 
  className = '', 
  style = {} 
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label="Connect with Strava"
    className={`strava-connect-btn ${className}`}
    style={{
      background: '#FC4C02',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '4px',
      padding: '0.75rem 1.25rem',
      fontSize: '0.95rem',
      fontWeight: 700,
      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      boxShadow: '0 4px 14px rgba(252, 76, 2, 0.35)',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      letterSpacing: '0.01em',
      opacity: disabled ? 0.7 : 1,
      ...style
    }}
    onMouseOver={(e) => {
      if (!disabled) {
        e.currentTarget.style.background = '#E34402';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 6px 18px rgba(252, 76, 2, 0.5)';
      }
    }}
    onMouseOut={(e) => {
      if (!disabled) {
        e.currentTarget.style.background = '#FC4C02';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(252, 76, 2, 0.35)';
      }
    }}
  >
    {/* Isotipo oficial de Strava */}
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7.18 14.173h4.172" />
    </svg>
    <span>Connect with Strava</span>
  </button>
);

/**
 * Distintivo oficial obligatorio "Powered by Strava" (Directrices de Marca de Strava API - Section 2).
 * Exigido para cualquier vista, tarjeta o gráfico que renderice datos derivados de Strava.
 */
export const PoweredByStrava = ({ style = {}, light = false }) => (
  <div 
    className="powered-by-strava"
    style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '6px', 
      fontSize: '0.72rem', 
      color: light ? '#cbd5e1' : '#94A3B8',
      fontWeight: 500,
      userSelect: 'none',
      ...style 
    }}
    title="Datos e información deportiva procesados mediante la API de Strava"
  >
    <span style={{ letterSpacing: '0.02em' }}>Powered by</span>
    <span style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '4px',
      color: '#FC4C02', 
      fontWeight: 800,
      fontStyle: 'italic',
      letterSpacing: '0.04em'
    }}>
      <svg 
        width="14" 
        height="14" 
        viewBox="0 0 24 24" 
        fill="#FC4C02" 
        aria-hidden="true"
        style={{ display: 'inline-block' }}
      >
        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7.18 14.173h4.172" />
      </svg>
      STRAVA
    </span>
  </div>
);

/**
 * Botón / Enlace oficial reglamentario "Ver en Strava" (Directrices de Marca de Strava API - Section 3).
 * Redirige a la URL canónica de la actividad en Strava.
 */
export const ViewOnStravaButton = ({ 
  activityId, 
  style = {}, 
  compact = false,
  className = '' 
}) => {
  if (!activityId) return null;
  const stravaUrl = `https://www.strava.com/activities/${activityId}`;

  return (
    <a
      href={stravaUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`view-on-strava-link ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? '4px' : '6px',
        background: 'rgba(252, 76, 2, 0.12)',
        color: '#FC4C02',
        border: '1px solid rgba(252, 76, 2, 0.35)',
        borderRadius: '6px',
        padding: compact ? '0.25rem 0.55rem' : '0.4rem 0.8rem',
        fontSize: compact ? '0.72rem' : '0.8rem',
        fontWeight: 700,
        textDecoration: 'none',
        lineHeight: 1,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        ...style
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = '#FC4C02';
        e.currentTarget.style.color = '#FFFFFF';
        e.currentTarget.style.borderColor = '#FC4C02';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 3px 10px rgba(252, 76, 2, 0.35)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = 'rgba(252, 76, 2, 0.12)';
        e.currentTarget.style.color = '#FC4C02';
        e.currentTarget.style.borderColor = 'rgba(252, 76, 2, 0.35)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      title="Abrir esta actividad en Strava"
    >
      <svg 
        width={compact ? 12 : 14} 
        height={compact ? 12 : 14} 
        viewBox="0 0 24 24" 
        fill="currentColor" 
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7.18 14.173h4.172" />
      </svg>
      <span>Ver en Strava</span>
      <ExternalLink size={compact ? 11 : 13} style={{ opacity: 0.8, flexShrink: 0 }} />
    </a>
  );
};

export default StravaConnectButton;
